import type { SortOrder } from 'src/types/pagination';
import type { AllocationAuditTrailDTO, AllocationAuditTrailType } from 'src/types/dto';
import type { GridSortModel, GridPaginationModel, GridRenderCellParams } from '@mui/x-data-grid';

import dayjs from 'dayjs';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Typography, Tooltip } from '@mui/material';
import { gridClasses } from '@mui/x-data-grid';

import { AppDataGrid } from 'src/components/app-data-grid';
import { EmptyContent } from 'src/components/empty-content';
import { Label } from 'src/components/label';

import { usePaginatedApi } from 'src/hooks/use-paginated-api';
import { endpoints } from 'src/utils/axios';
import { convertToTimezone } from 'src/utils/timezone';

import type { LabelColor } from 'src/components/label/types';

const TYPE_LABEL_COLOR: Record<AllocationAuditTrailType, LabelColor> = {
  success: 'success',
  failure: 'error',
  warning: 'warning',
  info: 'info',
};

interface AllocationAuditTrailsTabProps {
  solutionId: number | string;
  activityId: number | string;
}

export function AllocationAuditTrailsTab({
  solutionId,
  activityId,
}: AllocationAuditTrailsTabProps) {
  const { t } = useTranslation('activities');
  const loggedInUserTimezone = dayjs.tz.guess();
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 50,
  });
  const [sortModel, setSortModel] = useState<GridSortModel>([
    { field: 'createdAt', sort: 'desc' },
  ]);

  const columns = [
    {
      field: 'metadata',
      headerName: t('activityModal.allocationAuditTrail.columns.auditMessage'),
      flex: 2,
      minWidth: 200,
      sortable: false,
      renderCell: (params: GridRenderCellParams<AllocationAuditTrailDTO>) => {
        const message = params.row.metadata?.message;
        if (!message) {
          return '-';
        }
        return (
          <Tooltip title={message}>
            <Typography
              variant="body2"
              sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
            >
              {message}
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      field: 'type',
      headerName: t('activityModal.allocationAuditTrail.columns.type'),
      flex: 1,
      minWidth: 120,
      renderCell: (params: GridRenderCellParams<AllocationAuditTrailDTO>) => (
        <Label color={TYPE_LABEL_COLOR[params.row.type]} variant="soft">
          {t(`activityModal.allocationAuditTrail.types.${params.row.type}`)}
        </Label>
      ),
    },
    {
      field: 'inspectorName',
      headerName: t('activityModal.allocationAuditTrail.columns.inspector'),
      flex: 1,
      minWidth: 140,
      sortable: false,
      valueFormatter: (value: string | null) => value || '-',
    },
    {
      field: 'createdAtUtc',
      headerName: t('activityModal.allocationAuditTrail.columns.timestampUtc'),
      flex: 1,
      minWidth: 160,
      sortable: false,
      valueGetter: (_value: unknown, row: AllocationAuditTrailDTO) => row.createdAt,
      valueFormatter: (value: string) =>
        value ? dayjs.utc(value).format('DD MMM YY, HH:mm') : '-',
    },
    {
      field: 'createdAt',
      headerName: t('activityModal.allocationAuditTrail.columns.timestampLocal'),
      flex: 1,
      minWidth: 160,
      valueFormatter: (value: string) =>
        value ? convertToTimezone(value, loggedInUserTimezone).format('DD MMM YY, HH:mm') : '-',
    }
  ];

  const { data, isLoading, pagination } = usePaginatedApi<AllocationAuditTrailDTO>({
    endpoint: endpoints.solutions.allocationAuditTrail(solutionId, activityId),
    paginationParams: { page: paginationModel.page + 1, take: paginationModel.pageSize },
    filterParams: {
      sortOptions: sortModel.map((e) => ({
        field: e.field,
        order: e.sort?.toUpperCase() as SortOrder,
      })),
    },
  });

  return (
    <AppDataGrid
      checkboxSelection={false}
      disableRowSelectionOnClick
      disableColumnFilter
      rows={data || []}
      getRowId={(row) => row.id}
      columns={columns}
      loading={isLoading}
      columnHeaderHeight={32}
      rowHeight={40}
      pageSizeOptions={[50]}
      paginationModel={paginationModel}
      rowCount={pagination?.itemCount || 0}
      paginationMode="server"
      onPaginationModelChange={(newModel) => {
        setPaginationModel(newModel);
      }}
      sortModel={sortModel}
      onSortModelChange={(newModel) => {
        setSortModel(newModel);
      }}
      hideFooterPagination={(pagination?.itemCount || 0) <= 50}
      hideFooter={(pagination?.itemCount || 0) <= 50}
      slots={{
        noRowsOverlay: () => <EmptyContent title={t('common:list.empty')} />,
        noResultsOverlay: () => <EmptyContent title={t('common:list.empty')} />,
      }}
      sx={{
        '.MuiDataGrid-columnHeader:focus-within, .MuiDataGrid-cell:focus-within': {
          outline: 'none',
        },
        [`& .${gridClasses.cell}`]: {
          alignItems: 'center',
          display: 'inline-flex',
          padding: '8px',
          borderBottomStyle: 'solid',
        },
        [`& .${gridClasses.columnHeader}`]: {
          borderBottomStyle: 'solid',
        },
      }}
    />
  );
}
